import React, { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Content,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Indicators,
  Button,
  Modal,
  RadioGroup,
  RadioGroupItem,
  Label,
  Textarea,
} from 'ui'
import { useAdminProfileById } from '../../../queries/useAdminProfileById'
import { useDenyProfileMutation } from '../../../queries/useAdminProfileMutations'

enum ReasonForDenial {
  Spam = 'Spam',
  Inappropriate = 'Inappropriate content / email',
  Duplicate = 'Duplicate of another profile',
  Other = 'Other',
}

const denialFormSchema = yup.object().shape({
  message: yup.string().required('Message cannot be blank'),
  otherReason: yup.string().when('reason', {
    is: ReasonForDenial.Other,
    then: (schema) => schema.required('Other reason is required'),
    otherwise: (schema) => schema.optional(),
  }),
  reason: yup.string().required('Reason is required'),
})

type DenyFormValues = yup.InferType<typeof denialFormSchema>

interface UserProfileDenyViewProps {
  profileId: string
  onDismiss: () => void
}

export const UserProfileDenyView: React.FC<UserProfileDenyViewProps> = ({
  profileId,
  onDismiss,
}) => {
  const { isLoading: loading, data: profile } = useAdminProfileById(profileId)
  const denyProfileMutation = useDenyProfileMutation()

  const form = useForm<DenyFormValues>({
    defaultValues: {
      message:
        "We're sorry, but your account request has been denied. If you feel that this message has been received in error, please contact the Maricopa County Office of the School Superintendent at (602) 506-3866.",
      otherReason: '',
      reason: ReasonForDenial.Duplicate,
    },
    resolver: yupResolver(denialFormSchema),
  })

  const handleSubmitForm = async (values: DenyFormValues) => {
    try {
      const reason =
        values.reason === ReasonForDenial.Other
          ? values.otherReason
          : values.reason

      await denyProfileMutation.mutateAsync({
        id: profileId,
        reason,
        message: values.message,
      })

      onDismiss()
    } catch (error) {
      console.error('Error denying profile:', error)
    }
  }

  return (
    <Modal.View
      onDismiss={onDismiss}
      loading={loading}
      size={Modal.Size.medium}
    >
      <div className="flex-col flex p-6 w-full">
        <Content.HeadingSmall className="mb-4">
          Deny Access for {profile?.firstName} {profile?.lastName}
        </Content.HeadingSmall>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmitForm)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason for Denial</FormLabel>
                  <p className="text-sm text-gray-600 mb-2">
                    Add a short note for internal reference about why this
                    profile was denied.
                  </p>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="space-y-2"
                      disabled={form.formState.isSubmitting}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReasonForDenial.Duplicate}
                          id="duplicate"
                        />
                        <Label htmlFor="duplicate">
                          {ReasonForDenial.Duplicate}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReasonForDenial.Inappropriate}
                          id="inappropriate"
                        />
                        <Label htmlFor="inappropriate">
                          {ReasonForDenial.Inappropriate}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReasonForDenial.Spam}
                          id="spam"
                        />
                        <Label htmlFor="spam">{ReasonForDenial.Spam}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReasonForDenial.Other}
                          id="other"
                        />
                        <Label htmlFor="other">{ReasonForDenial.Other}</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('reason') === ReasonForDenial.Other && (
              <FormField
                control={form.control}
                name="otherReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Other Reason</FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        className="w-full p-2 border rounded"
                        placeholder="a short note as to why this profile was denied"
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Message</FormLabel>
                  <p className="text-sm text-gray-600 mb-2">
                    A brief message emailed to the user who submitted the
                    request.
                  </p>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="A brief message emailed to the user who submitted the request."
                      disabled={form.formState.isSubmitting}
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {denyProfileMutation.error && (
              <div className="text-red-600 text-sm">
                {denyProfileMutation.error.message}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onDismiss}
                disabled={
                  form.formState.isSubmitting || denyProfileMutation.isPending
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  form.formState.isSubmitting || denyProfileMutation.isPending
                }
              >
                {form.formState.isSubmitting ||
                denyProfileMutation.isPending ? (
                  <Indicators.Spinner size="sm" className="mr-2" />
                ) : null}
                Deny User
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </Modal.View>
  )
}
